import { MouseEvent, useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';

import CardComments from './comments/CardComments';

import type { IdeaComment } from '../../interfaces/IdeaComment';
import type { Participant } from '../../interfaces/Participant';

interface IdeaCardCommentsProps {
  ideaId: string;
  commentsEnabled: boolean;
  commentCount: number;
  comments: IdeaComment[];
  participants: Participant[];
}

const IdeaCardComments = ({
  ideaId,
  commentsEnabled,
  commentCount,
  comments,
  participants,
}: IdeaCardCommentsProps) => {
  const [commentsAnchorEl, setCommentsAnchorEl] = useState<null | HTMLElement>(null);
  const isCommentsOpen = Boolean(commentsAnchorEl);

  const theme = useTheme();
  const isSmallWindow = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!commentsEnabled && commentsAnchorEl) {
      setCommentsAnchorEl(null);
    }
  }, [commentsEnabled, commentsAnchorEl]);

  const handleCommentsClick = (event: MouseEvent<HTMLElement>) => {
    if (!commentsEnabled) {
      return;
    }

    event.stopPropagation();
    setCommentsAnchorEl((previousValue) =>
      previousValue ? null : (event.currentTarget as HTMLElement)
    );
  };

  if (!commentsEnabled) {
    return null;
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.2}
        sx={{
          mr: '-4px !important',
        }}
      >
        <IconButton
          size="small"
          onClick={handleCommentsClick}
          aria-label={`Show comments for idea ${ideaId}`}
          aria-describedby={isCommentsOpen ? `idea-comments-popover-${ideaId}` : undefined}
          sx={{
            borderRadius: 1,
            px: 0.5,
            pr: 0,
            py: 0.3,
            gap: 0,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 15, fontWeight: 700 }}>
            {commentCount}
          </Typography>
          <ModeCommentOutlinedIcon sx={{ height: 18 }} />
        </IconButton>
      </Stack>
      <Popover
        id={`idea-comments-popover-${ideaId}`}
        open={commentsEnabled && isCommentsOpen}
        anchorEl={commentsAnchorEl}
        onClose={() => setCommentsAnchorEl(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 32px)',
            maxWidth: 400,
            height: '70vh',
            maxHeight: 520,
            overflow: 'hidden',
            left: { xs: '16px', sm: 'auto' },
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 520,
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ overflow: 'auto', width: '100%', height: '100%', pb: 2 }}>
            <CardComments ideaId={ideaId} comments={comments} participants={participants} />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default IdeaCardComments;
